import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  editAppointmentRecord,
  fetchAppointmentDetails,
} from "@/lib/services/appointmentModalService";
import { getAppointmentRegistry } from "@/lib/appointment/appointmentRegistrySingleton";
import { invalidateRecordCaches, resyncCalendarCaches } from "@/lib/appointment/recordCaches";
import { hmFromMinutes } from "@/lib/time";
import type { AppointmentMoveUndo } from "@/lib/appointment/AppointmentRegistry";

/**
 * Reschedules an appointment: new time, new date, new staff member, or any combination.
 *
 * The record is re-fetched before saving rather than patched from the lighter timetable
 * copy, because `/admin-record/edit` diffs the whole document it is given — sending a
 * partial one would read as fields being cleared.
 *
 * Only `date`, `startTime`, `endTime` and the staff id are changed. The backend routes
 * `staff_id` and `specialistId` to the same column and reads `date`/`startTime` as salon-
 * local strings, deriving the stored UTC instant itself (`app/services/revision.py`);
 * `master_id` is not routed at all, so it is deliberately left out of the patch.
 */

export type MoveAppointmentVars = {
  appointmentId: number;
  /** Target date, YYYY-MM-DD in the salon zone. */
  date: string;
  /** Target start, salon-zone minutes from midnight. */
  startMin: number;
  /** Preserved across the move — dragging never resizes a card. */
  durationMin: number;
  staffId: number;
  staffName?: string;
  /** Where the appointment came from, so the lists it left are refetched too. */
  fromDate: string;
  fromStaffId: number;
};

export function useMoveAppointment() {
  const queryClient = useQueryClient();

  return useMutation<any, unknown, MoveAppointmentVars, { undo: AppointmentMoveUndo | null }>({
    // Show the move at once — a drag that snaps back while the request is in flight
    // reads as a rejected drop.
    onMutate: async (vars) => {
      const undo = getAppointmentRegistry().applyMove(vars.appointmentId, {
        date: vars.date,
        startMin: vars.startMin,
        staffId: vars.staffId,
        staffName: vars.staffName,
      });
      resyncCalendarCaches(queryClient);
      return { undo };
    },

    mutationFn: async (vars) => {
      const record = await fetchAppointmentDetails(vars.appointmentId);
      if (!record) throw new Error(`Appointment ${vars.appointmentId} not found`);

      // The detail fetch merges server state back into the registry, which undoes the
      // optimistic placement; re-apply it so the card does not flicker back mid-request.
      getAppointmentRegistry().applyMove(vars.appointmentId, {
        date: vars.date,
        startMin: vars.startMin,
        staffId: vars.staffId,
        staffName: vars.staffName,
      });
      resyncCalendarCaches(queryClient);

      const payload: any = {
        ...record,
        date: vars.date,
        startTime: hmFromMinutes(vars.startMin),
        endTime: hmFromMinutes(vars.startMin + vars.durationMin),
        staff_id: vars.staffId,
        specialistId: vars.staffId,
        staff: record.staff
          ? { ...record.staff, id: vars.staffId, ...(vars.staffName ? { name: vars.staffName } : {}) }
          : record.staff,
        ...(vars.staffName ? { staff_name: vars.staffName } : {}),
      };

      return editAppointmentRecord(payload);
    },

    onError: (error, _vars, ctx) => {
      console.error("[calendar] failed to move appointment:", error);
      getAppointmentRegistry().revertMove(ctx?.undo);
      resyncCalendarCaches(queryClient);
    },

    onSettled: (_data, _error, vars) => {
      invalidateRecordCaches(queryClient, {
        recordId: vars.appointmentId,
        dates: [vars.fromDate, vars.date],
        staffIds: [vars.fromStaffId, vars.staffId],
      });
    },
  });
}
